import React, { useEffect, useState, useContext } from "react";
import {
  Container,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  MenuItem,
  Chip,
  Pagination,
} from "@mui/material";
import {
  Storefront as StorefrontIcon,
  LocationOn as LocationOnIcon,
  Category as CategoryIcon,
  Star as StarIcon,
  CurrencyRupee as CurrencyRupeeIcon,
} from "@mui/icons-material";
import axios from "../services/axiosInstance";
import API_BASE_URL from "../../src/config";
import { UserContext } from "../context/UserContext";

const Offers = () => {
  const { user } = useContext(UserContext);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCity, setUserCity] = useState("");
  const [category, setCategory] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [totalOffers, setTotalOffers] = useState(0);

  const categories = ["Home & Living","Business Support","Travel & Logistics","Event & Party Services","Education & Learning","Digital & Tech Services","Special Services"];
  const priceRanges = ["0-500", "500-1000", "1000-5000", "5000+"];
  const sortOptions = [
    { label: "Newest", value: "newest" },
    { label: "Highest Discount", value: "highest_discount" },
    { label: "Most Popular", value: "most_popular" },
  ];

  useEffect(() => {
    if (user?.id) {
      fetchUserCity(user.id);
    }
  }, [user]);

  const fetchUserCity = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/${userId}`);
      const city = response.data.interested_city;
      if (city) setUserCity(city);
    } catch (err) {
      console.error("Error fetching user city:", err);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, [user, page, category, priceRange, sortBy, search, userCity]);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/offers`, {
        params: {
          limit: 1000,
          category,
          priceRange,
          sortBy,
          search,
        },
      });

      let allOffers = response.data.offers || [];

      // Show filtered data if the user is logged in
      const filteredOffers = user
        ? userCity
          ? allOffers.filter(
              (offer) =>
                offer.location &&
                offer.location.toLowerCase().includes(userCity.toLowerCase())
            )
          : allOffers
        : allOffers; // If not logged in, show all data

      const paginated = filteredOffers.slice((page - 1) * 6, page * 6);

      setOffers(paginated);
      setTotalOffers(filteredOffers.length);
    } catch (err) {
      console.error("Error fetching offers:", err);
      setError("Failed to load offers. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5, mb: 3 }}>
      <Typography variant="h4" color="primary" fontWeight="bold" textAlign="center" mb={3}>
        Business Offers
      </Typography>

      {userCity && user && (
        <Typography variant="subtitle1" textAlign="center" mb={2}>
          Showing offers in <strong style={{ color: "#0653a2" }}>{userCity}</strong>
        </Typography>
      )}

      <Typography variant="body1" textAlign="center" sx={{ mb: 4 }}>
        Find exclusive discounts and deals from local businesses.
      </Typography>

      {/* Filters */}
      {user && (
        <Box sx={{ display: "flex", gap: 5, mb: 3, flexWrap: "wrap" }}>
          <TextField
            label="Search Offers..."
            variant="outlined"
            size="medium"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 250 }}
          />
          <TextField
            select
            label="Category"
            variant="outlined"
            size="medium"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ width: 250 }}
          >
            {categories.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Price Range"
            variant="outlined"
            size="medium"
            value={priceRange}
            onChange={(e) => setPriceRange(e.target.value)}
            sx={{ width: 250 }}
          >
            {priceRanges.map((range) => (
              <MenuItem key={range} value={range}>
                {range}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Sort By"
            variant="outlined"
            size="medium"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            sx={{ width: 250 }}
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center">{error}</Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {offers.length > 0 ? (
              offers.map((offer) => (
                <Grid item xs={12} sm={6} md={4} key={offer.id}>
                  <Card
                    sx={{
                      p: 2,
                      boxShadow: 3,
                      position: "relative",
                      bgcolor: offer.featured ? "rgba(255, 223, 186, 0.3)" : "white",
                    }}
                  >
                    <CardContent>
                      {offer.featured && (
                        <Chip
                          label="Featured"
                          color="secondary"
                          icon={<StarIcon />}
                          sx={{ position: "absolute", top: 10, right: 10 }}
                        />
                      )}
                      <Typography variant="h6" fontWeight="bold">
                        {offer.title}
                      </Typography>
                      <Box display="flex" alignItems="center" mt={1}>
                        <StorefrontIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{offer.business}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <LocationOnIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{offer.location}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <CategoryIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{offer.category}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" mt={1}>
                        <CurrencyRupeeIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="body2">{offer.discount_price}</Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Typography variant="body1" textAlign="center" sx={{ width: "100%", mt: 3 }}>
                No offers available at the moment.
              </Typography>
            )}
          </Grid>

          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={Math.ceil(totalOffers / 6)}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </Container>
  );
};

export default Offers;
